import React, { useState, useEffect, useRef, useMemo } from 'react';
import EmailEditor from 'react-email-editor';
import {
    Container,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Typography,
    makeStyles,
    Chip,
    Tabs,
    Tab,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TablePagination,
    Paper,
    Grid,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Checkbox,
    Tooltip,
    Collapse,
    CircularProgress,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import TextField from '@material-ui/core/TextField';
import DeleteIcon from '@material-ui/icons/Delete';
import RepeatIcon from '@material-ui/icons/Repeat';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import DesktopMacIcon from '@material-ui/icons/DesktopMac';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import { RRule } from 'rrule';
import SRCards from '../../components/SRCards';
import CompanyApi from '../../services/company.service';

const RESPONSIVE_PROMPT = `Convert this HTML to render correctly on the following devices: iPhone 15 (~390px wide), Samsung Galaxy S25 (~393px wide), iPad Mini (~768px wide), iPad Pro 11" (~834px wide), iPad Pro 12.9" (~1024px wide), and Samsung Galaxy Tab (~800px wide). Use responsive CSS with media queries for these breakpoints — max-width: 100%, flexbox or CSS grid for layouts, relative units (%, rem, vw) instead of fixed px widths. Add a viewport meta tag if missing. Ensure text is readable without zooming, images scale proportionally, and buttons are at least 44px tall for easy tapping. Do not use fixed pixel widths on any container.`;

const CRM_TARGET_ROLES = ['admin', 'dispatcher', 'office', 'manager'];
const MOBILE_TARGET_ROLES = ['technician', 'helper', 'technician_restricted'];

const SCHEDULED_STATUS = {
    SCHEDULED: 'scheduled',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
};

const OCCURRENCE_STATUS = {
    PENDING: 'pending',
    FIRED: 'fired',
    SKIPPED: 'skipped',
};

const useStyles = makeStyles({
    section: {
        marginTop: 24,
        marginBottom: 16,
    },
    editorWrapper: {
        marginTop: 24,
        marginBottom: 8,
        border: '1px solid #ddd',
        borderRadius: 4,
        overflow: 'hidden',
    },
    buttonGroup: {
        display: 'flex',
        gap: 12,
        marginTop: 24,
        marginBottom: 32,
    },
    tabContent: {
        paddingTop: 24,
    },
    thumbnail: {
        width: 180,
        height: 120,
        overflow: 'hidden',
        border: '1px solid #eee',
        borderRadius: 4,
        cursor: 'pointer',
        position: 'relative' as const,
        backgroundColor: '#fafafa',
    },
    thumbnailInner: {
        width: 900,
        height: 600,
        transform: 'scale(0.2)',
        transformOrigin: 'top left',
        pointerEvents: 'none' as const,
        overflow: 'hidden',
    },
    templateCard: { height: '100%', display: 'flex', flexDirection: 'column' as const },
    templateCardActions: { marginTop: 'auto', justifyContent: 'space-between' },
    selectAllRow: { display: 'flex', alignItems: 'center', marginBottom: 8 },
    recurringFormRow: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' as const },
    occurrenceRow: { backgroundColor: '#f9f9f9' },
});

const HtmlThumbnail = ({ html, onClick, classes }: { html: string; onClick: () => void; classes: any }) => (
    <div className={classes.thumbnail} onClick={onClick}>
        <iframe
            srcDoc={html}
            title="preview"
            scrolling="no"
            sandbox="allow-same-origin"
            style={{
                width: 900,
                height: 600,
                transform: 'scale(0.2)',
                transformOrigin: 'top left',
                pointerEvents: 'none',
                border: 'none',
            }}
        />
    </div>
);

const getRecurringStatus = (a: any) => {
    if (!a.isRecurring) return null;
    if (a.scheduledStatus === SCHEDULED_STATUS.CANCELLED) return 'Deactivated';
    if (a.scheduledStatus === SCHEDULED_STATUS.COMPLETED) return 'Completed';
    if (a.scheduledStatus === SCHEDULED_STATUS.SCHEDULED && a.occurrencesFired === 0) return 'Scheduled';
    if (a.scheduledStatus === SCHEDULED_STATUS.SCHEDULED && a.occurrencesFired > 0) return 'In Progress';
    return null;
};

const recurringStatusColor = (status: string | null) => {
    if (status === 'Scheduled') return { backgroundColor: '#e3f2fd', color: '#1565c0' };
    if (status === 'In Progress') return { backgroundColor: '#fff8e1', color: '#e65100' };
    if (status === 'Completed') return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
    if (status === 'Deactivated') return { backgroundColor: '#fce4ec', color: '#c62828' };
    return {};
};

const BannerAnnouncement = () => {
    const classes = useStyles();
    const apiservices = new CompanyApi();
    const emailEditorRef = useRef<any>(null);

    const [activeTab, setActiveTab] = useState(0);
    const [companyList, setCompanyList] = useState<any[]>([]);
    const [snackBar, setSnackBar] = useState('');
    const [pageResponse, setPageResponse] = useState('');
    const [loading, setLoading] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);

    // Create tab
    const [selectedCompanies, setSelectedCompanies] = useState<any[]>([]);

    const [editorReady, setEditorReady] = useState(false);
    const [pendingPublish, setPendingPublish] = useState<{ html: string; design: any; imageUrl?: string } | null>(null);
    const [pendingDesignLoad, setPendingDesignLoad] = useState<any>(null);

    // Upload HTML mode
    const [uploadMode, setUploadMode] = useState(false);
    const [uploadedHtml, setUploadedHtml] = useState('');
    const [uploadedFileName, setUploadedFileName] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Save as template dialog
    const [saveTemplateDialog, setSaveTemplateDialog] = useState(false);
    const [pendingTemplateData, setPendingTemplateData] = useState<{ html: string; design: any } | null>(null);
    const [templateName, setTemplateName] = useState('');

    // History tab
    const [announcements, setAnnouncements] = useState<any[]>([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);

    // Templates tab
    const [templates, setTemplates] = useState<any[]>([]);
    const [templatesLoaded, setTemplatesLoaded] = useState(false);
    const [templatePlatform, setTemplatePlatform] = useState<'crm' | 'mobile'>('crm');

    // Publish mode dialog (now vs schedule)
    const [publishModeDialog, setPublishModeDialog] = useState(false);
    const [publishMode, setPublishMode] = useState<'now' | 'recurring'>('now');

    // Recurrence config
    const [recurrenceConfig, setRecurrenceConfig] = useState({
        startDate: '',
        repeatEvery: 1,
        repeatUnit: 'month',
        endsBy: 'occurrences' as 'occurrences' | 'date',
        maxOccurrences: 4,
        endDate: '',
    });

    // Edit recurrence dialog
    const [editRecurrenceDialog, setEditRecurrenceDialog] = useState(false);
    const [editRecurrenceTarget, setEditRecurrenceTarget] = useState<any>(null);
    const [editRecurrenceForm, setEditRecurrenceForm] = useState({ maxOccurrences: 0, endDate: '' });

    // Guidelines
    const [guidelinesOpen, setGuidelinesOpen] = useState(false);
    const [promptCopied, setPromptCopied] = useState(false);

    // Platform support (crm / mobile)
    const [platform, setPlatform] = useState<'crm' | 'mobile'>('crm');
    const [imageUploadMode, setImageUploadMode] = useState(false);
    const [uploadedImageUrl, setUploadedImageUrl] = useState('');
    const [uploadedImageFileName, setUploadedImageFileName] = useState('');
    const [imageUploading, setImageUploading] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState('');
    const imageFileInputRef = useRef<HTMLInputElement>(null);

    const companyMap = useMemo(() => {
        const map = new Map<string, string>();
        companyList.forEach((c: any) => map.set(String(c.value), c.label));
        return map;
    }, [companyList]);

    useEffect(() => {
        apiservices.getCompanies().then((data: any) => {
            if (Array.isArray(data)) {
                setCompanyList(data.map((c: any) => ({ label: `${c.name} (${c.id})`, value: c.id })));
            }
        });
    }, []);

    useEffect(() => {
        if (pendingDesignLoad && editorReady && emailEditorRef.current?.editor) {
            emailEditorRef.current.editor.loadDesign(pendingDesignLoad);
            setPendingDesignLoad(null);
        }
    }, [pendingDesignLoad, editorReady]);

    const handleTabChange = (_: any, newValue: number) => {
        setActiveTab(newValue);
        if (newValue === 1 && !historyLoaded) loadHistory();
        if (newValue === 2 && !templatesLoaded) loadTemplates(templatePlatform);
    };

    const loadHistory = async () => {
        setHistoryLoading(true);
        try {
            const data = await apiservices.getBannerAnnouncements();
            setAnnouncements(Array.isArray(data) ? data : []);
            setHistoryLoaded(true);
        } catch {
            showSnack('error', 'Failed to load announcement history.');
        } finally {
            setHistoryLoading(false);
        }
    };

    const loadTemplates = async (forPlatform: 'crm' | 'mobile' = templatePlatform) => {
        try {
            const data = await apiservices.getAnnouncementTemplates(forPlatform);
            setTemplates(Array.isArray(data) ? data : []);
            setTemplatesLoaded(true);
        } catch {
            showSnack('error', 'Failed to load templates.');
        }
    };

    const handleTemplatePlatformChange = (newPlatform: 'crm' | 'mobile') => {
        setTemplatePlatform(newPlatform);
        setTemplatesLoaded(false);
        loadTemplates(newPlatform);
    };

    const showSnack = (type: string, message: string) => {
        setSnackBar(type);
        setPageResponse(message);
    };

    // ─── Platform ─────────────────────────────────────────────────────────────

    const handleCopyPrompt = () => {
        navigator.clipboard.writeText(RESPONSIVE_PROMPT);
        setPromptCopied(true);
        setTimeout(() => setPromptCopied(false), 2000);
    };

    const handlePlatformChange = (newPlatform: 'crm' | 'mobile') => {
        setPlatform(newPlatform);
        setImageUploadMode(false);
        setUploadedImageUrl('');
        setUploadedImageFileName('');
        setTemplatesLoaded(false);
        if (newPlatform === 'mobile') setUploadMode(true);
    };

    const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImageUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const result = await apiservices.uploadAnnouncementImage(formData);
            setUploadedImageUrl(result.mobileImageUrl);
            setUploadedImageFileName(file.name);
        } catch (err: any) {
            showSnack('error', err?.message || 'Failed to upload image.');
        } finally {
            setImageUploading(false);
            e.target.value = '';
        }
    };

    // ─── Create Tab ───────────────────────────────────────────────────────────

    const allSelected = companyList.length > 0 && selectedCompanies.length === companyList.length;

    const handleSelectAll = () => {
        setSelectedCompanies(allSelected ? [] : companyList);
    };

    const onEditorReady = () => setEditorReady(true);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setUploadedHtml((ev.target?.result as string) || '');
            setUploadedFileName(file.name);
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const exportAndThen = (callback: (html: string, design: any) => void) => {
        emailEditorRef.current.editor.exportHtml((data: any) => {
            callback(data.html, data.design);
        }, { cleanup: true });
    };

    const handlePreview = () => {
        if (platform === 'mobile' && imageUploadMode) {
            if (!uploadedImageUrl) { showSnack('error', 'Please upload an image first.'); return; }
            setPreviewImageUrl(uploadedImageUrl);
            setPreviewHtml('');
            setPreviewOpen(true);
            return;
        }
        if (uploadMode) {
            if (!uploadedHtml) { showSnack('error', 'Please upload an HTML file first.'); return; }
            setPreviewHtml(uploadedHtml);
            setPreviewImageUrl('');
            setPreviewOpen(true);
            return;
        }
        exportAndThen((html) => { setPreviewHtml(html); setPreviewImageUrl(''); setPreviewOpen(true); });
    };

    const handlePublish = () => {
        if (platform === 'mobile' && imageUploadMode) {
            if (!uploadedImageUrl) { showSnack('error', 'Please upload an image first.'); return; }
            openPublishModeDialog('', null, uploadedImageUrl);
            return;
        }
        if (uploadMode) {
            if (!uploadedHtml) { showSnack('error', 'Please upload an HTML file first.'); return; }
            openPublishModeDialog(uploadedHtml, null);
            return;
        }
        exportAndThen((html, design) => {
            if (!html) { showSnack('error', 'Editor content is empty.'); return; }
            openPublishModeDialog(html, design);
        });
    };

    const openPublishModeDialog = (html: string, design: any, imageUrl?: string) => {
        if (!selectedCompanies.length) { showSnack('error', 'Please select at least one company.'); return; }
        setPendingPublish({ html, design, imageUrl });
        setPublishMode('now');
        setPublishModeDialog(true);
    };

    const handlePublishModeConfirm = () => {
        setPublishModeDialog(false);
        if (pendingPublish) submitPublish(pendingPublish.html, pendingPublish.design, publishMode, pendingPublish.imageUrl);
    };

    const submitPublish = async (html: string, design: any, mode: 'now' | 'recurring' = publishMode, imageUrl?: string) => {
        if (mode === 'recurring' && !recurrenceConfig.startDate) {
            showSnack('error', 'Please select a start date for the recurring announcement.');
            return;
        }
        setLoading(true);
        try {
            const createdBy = localStorage.getItem('email') || '';
            const companyIds = allSelected ? ['*'] : selectedCompanies.map((c: any) => c.value);
            const isRecurring = mode === 'recurring';
            const targetRoles = platform === 'crm' ? CRM_TARGET_ROLES : MOBILE_TARGET_ROLES;
            const body: any = { platform, companyIds, targetRoles, createdBy, isRecurring };
            if (imageUrl) {
                body.mobileImageUrl = imageUrl;
            } else {
                body.htmlContent = btoa(Array.from(new TextEncoder().encode(html)).map(b => String.fromCharCode(b)).join(''));
                if (design != null) body.designJson = design;
            }
            if (isRecurring) {
                const freqMap: any = { day: RRule.DAILY, week: RRule.WEEKLY, month: RRule.MONTHLY, year: RRule.YEARLY };
                const rruleOptions: any = {
                    freq: freqMap[recurrenceConfig.repeatUnit] || RRule.MONTHLY,
                    interval: recurrenceConfig.repeatEvery,
                    dtstart: new Date(recurrenceConfig.startDate),
                };
                if (recurrenceConfig.endsBy === 'occurrences') {
                    rruleOptions.count = recurrenceConfig.maxOccurrences;
                } else if (recurrenceConfig.endDate) {
                    rruleOptions.until = new Date(recurrenceConfig.endDate);
                }
                body.rrule = new RRule(rruleOptions).toString();
            }
            await apiservices.createBannerAnnouncement(body);
            showSnack('success', isRecurring ? 'Recurring announcement scheduled!' : 'Announcement published successfully!');
            setActiveTab(1);
            loadHistory();
        } catch (err: any) {
            showSnack('error', err?.message || 'Failed to publish announcement.');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAsTemplate = () => {
        if (uploadMode) {
            if (!uploadedHtml) { showSnack('error', 'Please upload an HTML file first.'); return; }
            setPendingTemplateData({ html: uploadedHtml, design: null });
            setTemplateName('');
            setSaveTemplateDialog(true);
            return;
        }
        exportAndThen((html, design) => {
            if (!html) { showSnack('error', 'Editor content is empty.'); return; }
            setPendingTemplateData({ html, design });
            setTemplateName('');
            setSaveTemplateDialog(true);
        });
    };

    const submitSaveTemplate = async () => {
        if (!templateName.trim()) { showSnack('error', 'Please enter a template name.'); return; }
        setLoading(true);
        try {
            await apiservices.createAnnouncementTemplate({
                name: templateName.trim(),
                htmlContent: btoa(Array.from(new TextEncoder().encode(pendingTemplateData!.html)).map(b => String.fromCharCode(b)).join('')),
                ...(pendingTemplateData!.design != null && { designJson: pendingTemplateData!.design }),
                platform,
            });
            showSnack('success', 'Template saved successfully!');
            setSaveTemplateDialog(false);
            setTemplatesLoaded(false);
        } catch (err: any) {
            showSnack('error', err?.message || 'Failed to save template.');
        } finally {
            setLoading(false);
        }
    };

    // ─── History Tab ──────────────────────────────────────────────────────────

    const loadDesignIntoEditor = (designJson: any) => {
        if (!designJson) return;
        if (editorReady && emailEditorRef.current?.editor) {
            emailEditorRef.current.editor.loadDesign(designJson);
        } else {
            setPendingDesignLoad(designJson);
        }
    };

    const handleRepublish = (e: React.MouseEvent, announcement: any) => {
        e.stopPropagation();
        const companies = (announcement.companyIds || []).map((id: any) => ({
            label: companyMap.get(String(id)) || String(id),
            value: id,
        }));
        setSelectedCompanies(companies);
        setPlatform(announcement.platform || 'crm');
        if (announcement.mobileImageUrl) {
            setUploadMode(true);
            setImageUploadMode(true);
            setUploadedImageUrl(announcement.mobileImageUrl);
            setUploadedImageFileName('mobile-announcement-image');
        } else if (announcement.designJson) {
            setUploadMode(false);
            setImageUploadMode(false);
            loadDesignIntoEditor(announcement.designJson);
        } else {
            setUploadMode(true);
            setImageUploadMode(false);
            setUploadedHtml(announcement.htmlContent || '');
            const date = announcement.createdAt ? new Date(announcement.createdAt).toLocaleDateString('en-CA') : announcement.announcementId;
            setUploadedFileName(`announcement-${date}.html`);
        }
        setActiveTab(0);
    };

    const handleDeactivateFromHistory = async (e: React.MouseEvent, announcement: any) => {
        e.stopPropagation();
        setLoading(true);
        try {
            await apiservices.deactivateBannerAnnouncement({
                announcementId: announcement.announcementId,
                companyIds: announcement.companyIds,
                platform: announcement.platform || 'crm',
            });
            showSnack('success', 'Announcement deactivated successfully!');
            setHistoryLoaded(false);
            loadHistory();
        } catch (err: any) {
            showSnack('error', err?.message || 'Failed to deactivate.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelRecurring = async (e: React.MouseEvent, announcement: any) => {
        e.stopPropagation();
        setLoading(true);
        try {
            await apiservices.cancelRecurringAnnouncement(announcement.announcementId, announcement.companyIds, announcement.platform || 'crm');
            showSnack('success', 'Recurring announcement cancelled.');
            setHistoryLoaded(false);
            loadHistory();
        } catch (err: any) {
            showSnack('error', err?.message || 'Failed to cancel.');
        } finally {
            setLoading(false);
        }
    };

    const openEditRecurrence = (e: React.MouseEvent, announcement: any) => {
        e.stopPropagation();
        setEditRecurrenceTarget(announcement);
        const form = { maxOccurrences: 0, endDate: '' };
        if (announcement.rrule) {
            const rule = RRule.fromString(announcement.rrule);
            if (rule.options.count) form.maxOccurrences = rule.options.count;
            if (rule.options.until) form.endDate = new Date(rule.options.until).toISOString().slice(0, 10);
        }
        setEditRecurrenceForm(form);
        setEditRecurrenceDialog(true);
    };


    const resolveCompanyNames = (companyIds: any[]) => {
        if (!companyIds?.length) return '—';
        if (companyIds.length === 1 && companyIds[0] === '*') return 'All Companies';
        const names = companyIds.map((id: any) => companyMap.get(String(id)) || String(id));
        if (names.length <= 3) return names.join(', ');
        const overflow = names.slice(3);
        return (
            <span>
                {names.slice(0, 3).join(', ')}{' '}
                <Tooltip title={overflow.join(', ')} placement="top">
                    <span style={{ cursor: 'pointer', color: '#1976d2', fontWeight: 500 }}>+{overflow.length} more</span>
                </Tooltip>
            </span>
        );
    };

    const handleUseTemplate = (e: React.MouseEvent, template: any) => {
        e.stopPropagation();
        setPlatform(template.platform || 'crm');
        setImageUploadMode(false);
        if (template.designJson) {
            setUploadMode(false);
            loadDesignIntoEditor(template.designJson);
        } else {
            setUploadMode(true);
            setUploadedHtml(template.htmlContent || '');
            setUploadedFileName(`${template.name}.html`);
        }
        setActiveTab(0);
    };

    const handleDeleteTemplate = async (e: React.MouseEvent, templateId: string) => {
        e.stopPropagation();
        setLoading(true);
        try {
            await apiservices.deleteAnnouncementTemplate(templateId);
            showSnack('success', 'Template deleted.');
            setTemplates((prev) => prev.filter((t) => t.templateId !== templateId));
        } catch (err: any) {
            showSnack('error', err?.message || 'Failed to delete template.');
        } finally {
            setLoading(false);
        }
    };

    const toggleExpandRow = (announcementId: string) => {
        setExpandedRow((prev) => (prev === announcementId ? null : announcementId));
    };

    const occurrenceStatusIcon = (status: string) => {
        if (status === OCCURRENCE_STATUS.FIRED) return '✅';
        if (status === OCCURRENCE_STATUS.SKIPPED) return '⏭️';
        return '⏳';
    };

    return (
        <>
            <SRCards />
            <Container>
                <Typography variant="h5" className={classes.section}>Banner Announcements</Typography>
                <Alert severity="info" style={{ marginBottom: 16 }}>
                    Broadcast important messages to your end users as a popup. Users will see the announcement until they dismiss it.
                </Alert>

                <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="secondary" textColor="secondary">
                    <Tab label="Create" />
                    <Tab label="History" />
                    <Tab label="Templates" />
                </Tabs>

                {/* Tab 0 — Create */}
                <div style={{ display: activeTab === 0 ? 'block' : 'none' }} className={classes.tabContent}>
                    {/* Platform toggle */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                        <Typography variant="body2" style={{ marginRight: 4 }}>Platform:</Typography>
                        <Button size="small" variant={platform === 'crm' ? 'contained' : 'outlined'} color="secondary" startIcon={<DesktopMacIcon />} onClick={() => handlePlatformChange('crm')}>Web</Button>
                        <Button size="small" variant={platform === 'mobile' ? 'contained' : 'outlined'} color="secondary" startIcon={<PhoneAndroidIcon />} onClick={() => handlePlatformChange('mobile')}>Mobile</Button>
                    </div>

                    {/* Mode toggle */}
                    {platform === 'crm' ? (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                            <Button size="small" variant={!uploadMode ? 'contained' : 'outlined'} color="secondary" onClick={() => setUploadMode(false)}>Use Editor</Button>
                            <Button size="small" variant={uploadMode ? 'contained' : 'outlined'} color="secondary" onClick={() => setUploadMode(true)}>Upload HTML</Button>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                            <Button size="small" variant={!imageUploadMode ? 'contained' : 'outlined'} color="secondary" onClick={() => setImageUploadMode(false)}>Upload HTML</Button>
                            <Button size="small" variant={imageUploadMode ? 'contained' : 'outlined'} color="secondary" onClick={() => setImageUploadMode(true)}>Upload Image</Button>
                        </div>
                    )}

                    {/* Guidelines — hidden when CRM + editor mode */}
                    {!(platform === 'crm' && !uploadMode) && <div style={{ marginBottom: 16 }}>
                        <div
                            onClick={() => setGuidelinesOpen(g => !g)}
                            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' as const }}
                        >
                            <EmojiObjectsIcon style={{ fontSize: 18, color: '#f59e0b' }} />
                            <Typography variant="body2" style={{ fontWeight: 600, color: '#555' }}>Content Guidelines</Typography>
                            {guidelinesOpen ? <KeyboardArrowUpIcon fontSize="small" style={{ color: '#888' }} /> : <KeyboardArrowDownIcon fontSize="small" style={{ color: '#888' }} />}
                        </div>
                        <Collapse in={guidelinesOpen}>
                            <div style={{ marginTop: 10, padding: '12px 16px', backgroundColor: '#fffde7', borderRadius: 6, border: '1px solid #ffe082' }}>
                                {platform === 'mobile' && !imageUploadMode && (
                                    <>
                                        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>HTML for Mobile</Typography>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            <li><Typography variant="body2">Set <code>max-width: 440px</code> on your main container</Typography></li>
                                            <li><Typography variant="body2">Keep total content height under <strong>560px</strong></Typography></li>
                                            <li><Typography variant="body2">Use <code>%</code>, <code>rem</code>, or <code>vw</code> units — avoid fixed <code>px</code> widths</Typography></li>
                                            <li><Typography variant="body2">Do not use CSS <code>transform</code>, <code>scale</code>, or <code>zoom</code></Typography></li>
                                            <li><Typography variant="body2">Add <code>{'<meta name="viewport" content="width=device-width, initial-scale=1">'}</code></Typography></li>
                                            <li><Typography variant="body2">All links must use <code>target="_blank"</code> to open in a browser</Typography></li>
                                        </ul>
                                        <Typography variant="body2" style={{ fontWeight: 600, marginTop: 12, marginBottom: 6 }}>
                                            Make your HTML responsive — copy this prompt into ChatGPT / Claude:
                                        </Typography>
                                        <div style={{ backgroundColor: '#fff8e1', border: '1px solid #ffd54f', borderRadius: 4, padding: '10px 12px' }}>
                                            <Typography variant="caption" style={{ display: 'block', lineHeight: 1.7, color: '#555' }}>
                                                {RESPONSIVE_PROMPT}
                                            </Typography>
                                            <Button size="small" variant="outlined" style={{ marginTop: 8 }} onClick={handleCopyPrompt}>
                                                {promptCopied ? '✓ Copied!' : 'Copy Prompt'}
                                            </Button>
                                        </div>
                                    </>
                                )}
                                {platform === 'mobile' && imageUploadMode && (
                                    <>
                                        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>Image for Mobile</Typography>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            <li><Typography variant="body2">Recommended size: <strong>440×560px</strong> (portrait orientation)</Typography></li>
                                            <li><Typography variant="body2">Formats: <strong>JPEG or PNG</strong></Typography></li>
                                            <li><Typography variant="body2">Keep file size under <strong>1MB</strong> for fast loading</Typography></li>
                                            <li><Typography variant="body2">Use high contrast text — the image will display across various screen sizes</Typography></li>
                                        </ul>
                                    </>
                                )}
                                {platform === 'crm' && (
                                    <>
                                        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>HTML for Web (CRM)</Typography>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            <li><Typography variant="body2">Design for a <strong>1200×700px</strong> container</Typography></li>
                                            <li><Typography variant="body2">Do not use CSS <code>transform</code>, <code>scale</code>, or <code>zoom</code> — content will overflow and get clipped</Typography></li>
                                            <li><Typography variant="body2">All links must use <code>target="_blank"</code> to open in a new tab</Typography></li>
                                            <li><Typography variant="body2">Avoid JavaScript — it will not execute inside the preview iframe</Typography></li>
                                        </ul>
                                    </>
                                )}
                            </div>
                        </Collapse>
                    </div>}

                    <div className={classes.selectAllRow}>
                        <Checkbox
                            checked={allSelected}
                            indeterminate={selectedCompanies.length > 0 && !allSelected}
                            onChange={handleSelectAll}
                            color="secondary"
                        />
                        <Typography variant="body2">Select All Companies</Typography>
                    </div>
                    <Autocomplete
                        multiple
                        options={companyList}
                        getOptionLabel={(option: any) => option.label}
                        getOptionSelected={(option: any, value: any) => option.value === value.value}
                        value={selectedCompanies}
                        onChange={(_e: any, value: any) => setSelectedCompanies(value)}
                        renderTags={(value: any[], getTagProps: any) => {
                            const MAX_VISIBLE = 3;
                            if (value.length <= MAX_VISIBLE) {
                                return value.map((option: any, index: number) => (
                                    <Chip key={option.value} label={option.label} size="small" {...getTagProps({ index })} />
                                ));
                            }
                            const visible = value.slice(0, MAX_VISIBLE);
                            const overflow = value.slice(MAX_VISIBLE);
                            return [
                                ...visible.map((option: any, index: number) => (
                                    <Chip key={option.value} label={option.label} size="small" {...getTagProps({ index })} />
                                )),
                                <Tooltip key="overflow" title={overflow.map((o: any) => o.label).join(', ')} placement="top">
                                    <Chip
                                        label={`+${overflow.length} more`}
                                        size="small"
                                        style={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 600, cursor: 'pointer' }}
                                    />
                                </Tooltip>,
                            ];
                        }}
                        renderInput={(params: any) => (
                            <TextField {...params} variant="outlined" label="Select Companies" placeholder="Search companies..." />
                        )}
                    />

                    <Typography variant="subtitle1" className={classes.section}>Announcement Content</Typography>

                    {/* Upload Image section — mobile only */}
                    {platform === 'mobile' && imageUploadMode && (
                        <div style={{ marginTop: 8, marginBottom: 16 }}>
                            <input
                                ref={imageFileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={handleImageFileChange}
                            />
                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                                Upload an image for the mobile announcement (JPEG/PNG, recommended: 1080×1920px portrait).
                            </Typography>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <Button variant="outlined" color="secondary" onClick={() => imageFileInputRef.current?.click()} disabled={imageUploading}>
                                    {imageUploading ? 'Uploading...' : 'Choose Image'}
                                </Button>
                                {uploadedImageFileName && (
                                    <>
                                        <Typography variant="body2" color="textSecondary">{uploadedImageFileName}</Typography>
                                        <IconButton size="small" onClick={() => { setUploadedImageUrl(''); setUploadedImageFileName(''); }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                )}
                            </div>
                            {uploadedImageUrl && (
                                <div style={{ marginTop: 12 }}>
                                    <Typography variant="caption" color="textSecondary">Preview:</Typography>
                                    <div style={{ marginTop: 4 }}>
                                        <img
                                            src={uploadedImageUrl}
                                            alt="mobile announcement"
                                            style={{ maxWidth: 200, maxHeight: 150, objectFit: 'contain', border: '1px solid #eee', borderRadius: 4, cursor: 'pointer' }}
                                            onClick={() => { setPreviewImageUrl(uploadedImageUrl); setPreviewHtml(''); setPreviewOpen(true); }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Upload HTML section */}
                    {(uploadMode && !(platform === 'mobile' && imageUploadMode)) && (
                        <div style={{ marginTop: 8, marginBottom: 16 }}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".html,.htm"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            <Typography variant="body1" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
                                {platform === 'mobile'
                                    ? 'Design your HTML for mobile screens. Use width: 100% and max-width: 440px on your main container. Keep total height under 560px.'
                                    : 'Design your HTML for a 1200×700px container. Do not use CSS transform, scale, or zoom — they will cause content to overflow and get clipped.'
                                }
                            </Typography>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <Button variant="outlined" color="secondary" onClick={() => fileInputRef.current?.click()}>
                                    Choose HTML File
                                </Button>
                                {uploadedFileName && (
                                    <>
                                        <Typography variant="body2" color="textSecondary">{uploadedFileName}</Typography>
                                        <IconButton size="small" onClick={() => { setUploadedHtml(''); setUploadedFileName(''); }}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                )}
                            </div>
                            {uploadedHtml && (
                                <div style={{ marginTop: 12 }}>
                                    <Typography variant="caption" color="textSecondary">Preview (click to enlarge):</Typography>
                                    <div style={{ marginTop: 4 }}>
                                        <HtmlThumbnail
                                            html={uploadedHtml}
                                            classes={classes}
                                            onClick={() => { setPreviewHtml(uploadedHtml); setPreviewImageUrl(''); setPreviewOpen(true); }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Unlayer editor — always mounted, hidden in upload/mobile mode */}
                    <div className={classes.editorWrapper} style={{ display: (uploadMode || platform === 'mobile') ? 'none' : 'block' }}>
                        <EmailEditor
                            ref={emailEditorRef}
                            onReady={onEditorReady}
                            projectId={237200}
                            options={{ displayMode: 'email' }}
                            minHeight="600px"
                        />
                    </div>

                    <div className={classes.buttonGroup}>
                        <Button variant="outlined" onClick={handlePreview}
                            disabled={
                                (platform === 'mobile' && imageUploadMode) ? !uploadedImageUrl
                                : uploadMode ? !uploadedHtml
                                : !editorReady
                            }>
                            Preview
                        </Button>
                        <Button variant="contained" color="secondary" onClick={handlePublish}
                            disabled={
                                (platform === 'mobile' && imageUploadMode) ? (!uploadedImageUrl || loading)
                                : uploadMode ? (!uploadedHtml || loading)
                                : (!editorReady || loading)
                            }>
                            Publish
                        </Button>
                        <Button variant="outlined" onClick={handleSaveAsTemplate}
                            disabled={
                                (platform === 'mobile' && imageUploadMode) || loading
                                || (uploadMode ? !uploadedHtml : !editorReady)
                            }>
                            Save as Template
                        </Button>
                    </div>
                </div>

                {/* Tab 1 — History */}
                {activeTab === 1 && (
                    <div className={classes.tabContent}>
                        <Paper>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell />
                                            <TableCell>Preview</TableCell>
                                            <TableCell>Created By</TableCell>
                                            <TableCell>Target Companies</TableCell>
                                            <TableCell>Created Date</TableCell>
                                            <TableCell>Platform</TableCell>
                                            <TableCell>Progress</TableCell>
                                            <TableCell>Next Fire</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {historyLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center" style={{ padding: 32 }}>
                                                    <CircularProgress size={28} color="secondary" />
                                                </TableCell>
                                            </TableRow>
                                        ) : announcements.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center">No announcements found.</TableCell>
                                            </TableRow>
                                        ) : (
                                            announcements
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((a: any) => {
                                                    const recurringStatus = getRecurringStatus(a);
                                                    const isExpanded = expandedRow === a.announcementId;
                                                    const totalOcc = a.rrule ? (() => { try { return RRule.fromString(a.rrule).all().length; } catch { return null; } })() : null;
                                                    const canEdit = a.isRecurring && (a.scheduledStatus === SCHEDULED_STATUS.SCHEDULED);
                                                    const canCancel = a.isRecurring && (a.scheduledStatus === SCHEDULED_STATUS.SCHEDULED);

                                                    return (
                                                        <React.Fragment key={a.announcementId}>
                                                            <TableRow>
                                                                <TableCell style={{ width: 40, padding: '0 8px' }}>
                                                                    {a.isRecurring && (
                                                                        <IconButton size="small" onClick={() => toggleExpandRow(a.announcementId)}>
                                                                            {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                                                        </IconButton>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {a.mobileImageUrl ? (
                                                                        <img
                                                                            src={a.mobileImageUrl}
                                                                            alt="mobile"
                                                                            style={{ width: 60, height: 40, objectFit: 'contain', cursor: 'pointer', border: '1px solid #eee', borderRadius: 2 }}
                                                                            onClick={() => { setPreviewImageUrl(a.mobileImageUrl); setPreviewHtml(''); setPreviewOpen(true); }}
                                                                        />
                                                                    ) : (
                                                                        <HtmlThumbnail html={a.htmlContent || ''} classes={classes} onClick={() => { setPreviewHtml(a.htmlContent || ''); setPreviewImageUrl(''); setPreviewOpen(true); }} />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>{a.createdBy || '—'}</TableCell>
                                                                <TableCell style={{ maxWidth: 220 }}>{resolveCompanyNames(a.companyIds)}</TableCell>
                                                                <TableCell>{a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '—'}</TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={a.platform === 'mobile' ? 'Mobile' : 'Web'}
                                                                        size="small"
                                                                        style={{
                                                                            backgroundColor: a.platform === 'mobile' ? '#e3f2fd' : '#e8f5e9',
                                                                            color: a.platform === 'mobile' ? '#1565c0' : '#2e7d32',
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {a.isRecurring && totalOcc ? `${a.occurrencesFired || 0} / ${totalOcc}` : '—'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {a.isRecurring && a.nextFireAt && a.scheduledStatus === SCHEDULED_STATUS.SCHEDULED
                                                                        ? new Date(a.nextFireAt).toLocaleDateString()
                                                                        : '—'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {recurringStatus ? (
                                                                        <Chip label={recurringStatus} size="small" style={recurringStatusColor(recurringStatus)} />
                                                                    ) : (
                                                                        <Chip
                                                                            label={a.isActive ? 'Completed' : 'Deactivated'}
                                                                            size="small"
                                                                            style={{
                                                                                backgroundColor: a.isActive ? '#e8f5e9' : '#fce4ec',
                                                                                color: a.isActive ? '#2e7d32' : '#c62828',
                                                                            }}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                                        {!a.isRecurring && (
                                                                            <Button size="small" variant="outlined" color="secondary" onClick={(e) => handleRepublish(e, a)}>Re-publish</Button>
                                                                        )}
                                                                        {!a.isRecurring && a.isActive && (
                                                                            <Button size="small" variant="outlined" disabled={loading} onClick={(e) => handleDeactivateFromHistory(e, a)}>Deactivate</Button>
                                                                        )}
                                                                        {canEdit && (
                                                                            <Button size="small" variant="outlined" color="secondary" disabled={loading} onClick={(e) => openEditRecurrence(e, a)}>Edit</Button>
                                                                        )}
                                                                        {canCancel && (
                                                                            <Button size="small" variant="outlined" disabled={loading} onClick={(e) => handleCancelRecurring(e, a)}>Deactivate</Button>
                                                                        )}
                                                                    </div>
                                                                </TableCell>
                                                            </TableRow>

                                                            {/* Occurrence History Expansion */}
                                                            {a.isRecurring && (
                                                                <TableRow className={classes.occurrenceRow}>
                                                                    <TableCell colSpan={9} style={{ paddingBottom: 0, paddingTop: 0 }}>
                                                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                            <Table size="small" style={{ margin: '8px 16px 8px 48px', width: 'calc(100% - 64px)' }}>
                                                                                <TableHead>
                                                                                    <TableRow>
                                                                                        <TableCell><strong>#</strong></TableCell>
                                                                                        <TableCell><strong>Scheduled At</strong></TableCell>
                                                                                        <TableCell><strong>Status</strong></TableCell>
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {a.rrule ? (() => {
                                                                                        try {
                                                                                            return RRule.fromString(a.rrule).all().map((date: Date, idx: number) => {
                                                                                                const occIndex = idx + 1;
                                                                                                const derivedStatus = occIndex <= (a.occurrencesFired || 0)
                                                                                                    ? 'fired'
                                                                                                    : a.scheduledStatus === 'cancelled'
                                                                                                        ? 'skipped'
                                                                                                        : 'pending';
                                                                                                const displayDate = occIndex === a.currentOccurrenceIndex && a.nextFireAt
                                                                                                    ? new Date(a.nextFireAt).toLocaleString()
                                                                                                    : occIndex === (a.occurrencesFired || 0) && a.updatedAt
                                                                                                        ? new Date(a.updatedAt).toLocaleString()
                                                                                                        : new Date(date).toLocaleString();
                                                                                                return (
                                                                                                    <TableRow key={occIndex}>
                                                                                                        <TableCell>{occIndex}</TableCell>
                                                                                                        <TableCell>{displayDate}</TableCell>
                                                                                                        <TableCell>{occurrenceStatusIcon(derivedStatus)} {derivedStatus}</TableCell>
                                                                                                    </TableRow>
                                                                                                );
                                                                                            });
                                                                                        } catch { return null; }
                                                                                    })() : null}
                                                                                </TableBody>
                                                                            </Table>
                                                                        </Collapse>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={announcements.length}
                                page={page}
                                onPageChange={(_: any, newPage: number) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e: any) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[5, 10, 25]}
                            />
                        </Paper>
                    </div>
                )}

                {/* Tab 2 — Templates */}
                {activeTab === 2 && (
                    <div className={classes.tabContent}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
                            <Typography variant="body2" style={{ marginRight: 4 }}>Platform:</Typography>
                            <Button size="small" variant={templatePlatform === 'crm' ? 'contained' : 'outlined'} color="secondary" startIcon={<DesktopMacIcon />} onClick={() => handleTemplatePlatformChange('crm')}>Web</Button>
                            <Button size="small" variant={templatePlatform === 'mobile' ? 'contained' : 'outlined'} color="secondary" startIcon={<PhoneAndroidIcon />} onClick={() => handleTemplatePlatformChange('mobile')}>Mobile</Button>
                        </div>
                        {templates.length === 0 ? (
                            <Typography variant="body1" color="textSecondary">
                                No templates saved yet. Design an announcement and click "Save as Template".
                            </Typography>
                        ) : (
                            <Grid container spacing={3}>
                                {templates.map((t: any) => (
                                    <Grid item xs={12} sm={6} md={4} key={t.templateId}>
                                        <Card className={classes.templateCard} variant="outlined">
                                            <CardContent>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                                    <Typography variant="subtitle1">{t.name}</Typography>
                                                    <Chip
                                                        label={t.platform === 'mobile' ? 'Mobile' : 'Web'}
                                                        size="small"
                                                        style={{
                                                            backgroundColor: t.platform === 'mobile' ? '#e3f2fd' : '#e8f5e9',
                                                            color: t.platform === 'mobile' ? '#1565c0' : '#2e7d32',
                                                        }}
                                                    />
                                                </div>
                                                <HtmlThumbnail
                                                    html={t.htmlContent || ''}
                                                    classes={classes}
                                                    onClick={() => { setPreviewHtml(t.htmlContent || ''); setPreviewImageUrl(''); setPreviewOpen(true); }}
                                                />
                                                <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: 'block' }}>
                                                    {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ''}
                                                </Typography>
                                            </CardContent>
                                            <CardActions className={classes.templateCardActions}>
                                                <Button size="small" variant="contained" color="secondary"
                                                    onClick={(e) => handleUseTemplate(e, t)}>
                                                    Use Template
                                                </Button>
                                                <IconButton size="small" disabled={loading}
                                                    onClick={(e) => handleDeleteTemplate(e, t.templateId)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </div>
                )}

                {/* Preview Dialog */}
                <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Announcement Preview</DialogTitle>
                    <DialogContent>
                        {previewImageUrl ? (
                            <div style={{ textAlign: 'center' }}>
                                <img src={previewImageUrl} alt="announcement preview" style={{ maxWidth: '100%', maxHeight: 500, objectFit: 'contain' }} />
                            </div>
                        ) : (
                            <iframe
                                srcDoc={previewHtml}
                                title="Announcement Preview"
                                style={{ width: '100%', height: 500, border: 'none' }}
                                sandbox="allow-same-origin"
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Publish Mode Dialog */}
                <Dialog open={publishModeDialog} onClose={() => setPublishModeDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Publish Announcement</DialogTitle>
                    <DialogContent>
                        <FormControl component="fieldset" style={{ marginBottom: 16 }}>
                            <FormLabel component="legend">When to publish?</FormLabel>
                            <RadioGroup value={publishMode} onChange={(e) => setPublishMode(e.target.value as 'now' | 'recurring')}>
                                <FormControlLabel value="now" control={<Radio color="secondary" />} label="Publish Now" />
                                <FormControlLabel value="recurring" control={<Radio color="secondary" />} label="Schedule as Recurring" />
                            </RadioGroup>
                        </FormControl>

                        {publishMode === 'recurring' && (
                            <div>
                                <div className={classes.recurringFormRow}>
                                    <TextField
                                        label="Start Date & Time"
                                        type="datetime-local"
                                        value={recurrenceConfig.startDate}
                                        onChange={(e) => setRecurrenceConfig({ ...recurrenceConfig, startDate: e.target.value })}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        size="small"
                                    />
                                </div>
                                <div className={classes.recurringFormRow}>
                                    <Typography variant="body2">Repeat Every</Typography>
                                    <TextField
                                        type="number"
                                        value={recurrenceConfig.repeatEvery}
                                        onChange={(e) => setRecurrenceConfig({ ...recurrenceConfig, repeatEvery: parseInt(e.target.value) || 1 })}
                                        inputProps={{ min: 1 }}
                                        variant="outlined"
                                        size="small"
                                        style={{ width: 80 }}
                                    />
                                    <Select
                                        value={recurrenceConfig.repeatUnit}
                                        onChange={(e: any) => setRecurrenceConfig({ ...recurrenceConfig, repeatUnit: e.target.value })}
                                        variant="outlined"
                                        style={{ minWidth: 100 }}
                                    >
                                        <MenuItem value="day">Day</MenuItem>
                                        <MenuItem value="week">Week</MenuItem>
                                        <MenuItem value="month">Month</MenuItem>
                                        <MenuItem value="year">Year</MenuItem>
                                    </Select>
                                </div>
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Ends</FormLabel>
                                    <RadioGroup value={recurrenceConfig.endsBy} onChange={(e) => setRecurrenceConfig({ ...recurrenceConfig, endsBy: e.target.value as 'occurrences' | 'date' })}>
                                        <FormControlLabel
                                            value="occurrences"
                                            control={<Radio color="secondary" />}
                                            label={
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    After
                                                    <TextField
                                                        type="number"
                                                        value={recurrenceConfig.maxOccurrences}
                                                        onChange={(e) => setRecurrenceConfig({ ...recurrenceConfig, maxOccurrences: parseInt(e.target.value) || 1 })}
                                                        inputProps={{ min: 1 }}
                                                        variant="outlined"
                                                        size="small"
                                                        style={{ width: 70 }}
                                                        disabled={recurrenceConfig.endsBy !== 'occurrences'}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    occurrences
                                                </div>
                                            }
                                        />
                                        <FormControlLabel
                                            value="date"
                                            control={<Radio color="secondary" />}
                                            label={
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    On date
                                                    <TextField
                                                        type="date"
                                                        value={recurrenceConfig.endDate}
                                                        onChange={(e) => setRecurrenceConfig({ ...recurrenceConfig, endDate: e.target.value })}
                                                        InputLabelProps={{ shrink: true }}
                                                        variant="outlined"
                                                        size="small"
                                                        disabled={recurrenceConfig.endsBy !== 'date'}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                            }
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button color="secondary" disabled={loading} onClick={handlePublishModeConfirm}>
                            {loading ? 'Processing...' : publishMode === 'recurring' ? 'Schedule' : 'Publish Now'}
                        </Button>
                        <Button onClick={() => setPublishModeDialog(false)}>Cancel</Button>
                    </DialogActions>
                </Dialog>

                {/* Edit Recurrence Dialog */}
                <Dialog open={editRecurrenceDialog} onClose={() => setEditRecurrenceDialog(false)} maxWidth="xs" fullWidth>
                    <DialogTitle>Edit Recurrence</DialogTitle>
                    <DialogContent>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
                            <TextField
                                label="Max Occurrences"
                                type="number"
                                value={editRecurrenceForm.maxOccurrences}
                                onChange={(e) => setEditRecurrenceForm({ ...editRecurrenceForm, maxOccurrences: parseInt(e.target.value) || 0 })}
                                inputProps={{ min: (editRecurrenceTarget?.occurrencesFired || 0) + 1 }}
                                variant="outlined"
                                size="small"
                                fullWidth
                                helperText={`Min: ${(editRecurrenceTarget?.occurrencesFired || 0) + 1} (already fired: ${editRecurrenceTarget?.occurrencesFired || 0})`}
                            />
                            <TextField
                                label="End Date"
                                type="date"
                                value={editRecurrenceForm.endDate}
                                onChange={(e) => setEditRecurrenceForm({ ...editRecurrenceForm, endDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button color="secondary" onClick={submitSaveTemplate} disabled={loading}>
                            {loading ? 'Saving...' : 'Save'}
                        </Button>
                        <Button onClick={() => setEditRecurrenceDialog(false)}>Cancel</Button>
                    </DialogActions>
                </Dialog>

                {/* Save as Template Dialog */}
                <Dialog open={saveTemplateDialog} onClose={() => setSaveTemplateDialog(false)}>
                    <DialogTitle>Save as Template</DialogTitle>
                    <DialogContent>
                        <TextField autoFocus label="Template Name" variant="outlined" fullWidth value={templateName} onChange={(e) => setTemplateName(e.target.value)} style={{ marginTop: 8 }} />
                    </DialogContent>
                    <DialogActions>
                        <Button color="secondary" onClick={submitSaveTemplate} disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button>
                        <Button onClick={() => setSaveTemplateDialog(false)}>Cancel</Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={snackBar !== ''} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    autoHideDuration={6000} onClose={() => setSnackBar('')}>
                    <Alert severity={snackBar === 'error' ? 'error' : 'success'} onClose={() => setSnackBar('')}>
                        {pageResponse}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
};

export default BannerAnnouncement;
